import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import {
  ITeachingSubjectItem,
  PaymentCheckoutWizardService,
  UserServiceCheckoutBlockType,
} from '@app/account/shared/payment-checkout-wizard/payment-checkout-wizard.service';
import { ChangableComponent } from '@app/models/changable.component';
import {
  AccountService,
  BillingService,
  ConfigService,
  ListFilterForProfile,
  UserServicesBffService,
} from '@core/services';
import { WebsocketService } from '@core/websocket';
import {
  Collection,
  IPagination,
  ITeachingSubject,
  Meeting,
  Subject,
  SubjectCategoryEnum,
  User,
  UserProfile,
  UserService,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { takeUntil } from 'rxjs';

@Component({
  selector: 'app-user-service-detail',
  templateUrl: './detail.component.html',
  styleUrls: ['./detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserServiceDetailComponent extends ChangableComponent implements OnInit {
  deviceService = inject(DeviceDetectorService);
  protected websocketService = inject(WebsocketService);
  protected userServicesService = inject(UserServicesBffService);
  protected configService = inject(ConfigService);
  protected models = inject(PaymentCheckoutWizardService);
  protected billingService = inject(BillingService);

  @Input() public set profile(profile: Partial<UserProfile>) {
    if (profile) {
      this.profileData = profile;
      if (this.subjects) {
        this.prepare();
      }
    }
  }
  @Input() public set teacher(teacher: Partial<User>) {
    this.teacherData = teacher;
  }
  @Input() public set content(value: Meeting | UserService | Collection) {
    this._content = value;
  }
  @Input() public set me(me: User) {
    this._me = me;
  }

  @Output() public cardFullInModal = new EventEmitter<UserService>();

  public _content: Meeting | UserService | Collection = null;
  public teachingSubjectsList: ITeachingSubjectItem[] = [];
  public selectedSubjectValue = -1;
  public services: UserServiceCheckoutBlockType = {
    items: [],
    loading: false,
    loaded: false,
    filter: {
      author_id: this.models.teacher.id,
    },
    pagination: {
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 1,
    },
  };
  public selectedServiceIndex = -1;
  public isOwnServiceError = false;

  private subjects: Subject[];
  private profileData: Partial<UserProfile>;
  private teacherData: Partial<User>;
  private _me: User;
  private userServices: UserService[];

  public ngOnInit() {
    this.configService.subjects$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (subjects) => {
        this.subjects = subjects;
        this.prepare();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  private prepare() {
    if (!this.profileData || !this.subjects) {
      return;
    }
    this.teachingSubjectsList = (this.profileData?.teaching_subjects || [])
      .filter((s) => s.subject_id > 0)
      .map((subject, index) => {
        const _subject = this.subjects.find((s) => s.id === +subject.subject_id);
        return {
          value: index,
          title: (_subject ? _subject.title : null) || '---',
          route: AccountService.accountRoute(this.models.teacher, ['teaching', subject.subject_id.toString()]),
          native: this.findSubjectById(subject)?.category_id === SubjectCategoryEnum.native_language,
          subject,
        };
      });
    this.selectedSubjectValue = this.teachingSubjectsList[0].value as number;
    this.loadAllServices();
    this.models.reset();
  }

  public selectSubject(value: number) {
    this.selectedSubjectValue = value;
    this.loadServices();
    this.resetCalendar();
  }

  private loadAllServices() {
    const _filter: ListFilterForProfile = { ...this.services.filter };
    const _pagination: IPagination = {
      page: 1,
      pageSize: 100,
    };

    this.userServicesService
      .getList({
        strategy: 'profile',
        filter: this.services.filter,
        pagination: { page: 1, pageSize: 100 },
        details: ['subject_id'],
      })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.userServices = response.list;
          const ids = response.list.map((el) => el.subject_id);
          this.teachingSubjectsList = this.teachingSubjectsList.filter(
            (el) => ids.indexOf(el.subject.subject_id) !== -1,
          );
          this.selectSubject(this.teachingSubjectsList[0].value as number);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected loadServices() {
    if (!this.models.teacher || !this.selectedSubject || this.services.loading) {
      return;
    }
    this.services.loading = true;

    // сброс мастера (вынести в модель)
    this.models.passed_steps['datetime'] = false;
    this.models.passed_steps['payment'] = false;
    this.detectChanges();

    this.userServicesService
      .getList({
        strategy: 'profile',
        filter: { ...this.services.filter, subject_id: this.selectedSubject?.subject?.subject_id },
        pagination: this.services.pagination,
      })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.services.items = response.list;
          this.services.pagination = response.pagination;
          this.services.loading = false;
          this.services.loaded = true;
          this.selectedServiceIndex = -1;
          this.models.init({
            teacher: this.models.teacher,
            moreContent: response.list,
          });
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.services.loading = false;
          this.detectChanges();
        },
      });
  }

  public get selectedSubject(): ITeachingSubjectItem {
    return this.teachingSubjectsList.find((_ts) => _ts.value === this.selectedSubjectValue);
  }

  public findSubjectById(teaching: ITeachingSubject): Subject {
    return this.subjects.find((_s) => +_s.id === +teaching?.subject_id);
  }

  protected resetCalendar() {
    this.models.resetCalendar();
  }

  public get isMe(): boolean {
    return this._me && this.models.teacher && this._me?.id === this.models.teacher.id;
  }

  public selectUserService(index: number) {
    if (this.isMe) {
      this.isOwnServiceError = true;
    }
    this.selectedServiceIndex = index;
    this.models.content = this.services.items[this.selectedServiceIndex];
    this.models.goNextStep('detail');
    this.resetCalendar();
  }

  public trackUserServiceByFn(index: number, userService: UserService): string {
    return userService.id;
  }

  public onToggleUserServiceInModal(service?: UserService) {
    this.cardFullInModal.emit(service);
  }
}
